import { Loader, PrimaryButton, SectionMessage } from "@certn/cascadia-react";
import qs, { ParsedQs } from "qs";
import { PropsWithChildren, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTimeout } from "usehooks-ts";

import { AuthHeading } from "./AuthHeading";
import { AuthLayout } from "./AuthLayout";
import { useAuth } from "./hooks/useAuth";
import { parseQueryParam } from "./utils";

interface Props extends PropsWithChildren {
  /* Will be called if the app fails to redirect and the user clicks continue. */
  onContinue: () => void;
}

export function AuthCallbackPage(props: Props) {
  const { t } = useTranslation();
  const { isLoading, logout } = useAuth();
  const [customErrorMessage, setCustomErrorMessage] = useState({
    title: "",
    content: "",
  });
  const params = qs.parse(window.location.search, { ignoreQueryPrefix: true });

  useEffect(() => {
    switch (params.error_description) {
      case "user is blocked":
        setCustomErrorMessage({
          title: t("Your account has been deactivated"),
          content: t(
            "An admin has deactivated your access to your company’s account. Contact your admin or email support@certn.co to reactivate your account."
          ),
        });
        break;
      default:
        setCustomErrorMessage({
          title: t("Authentication Error: {{error}}", {
            error: parseQueryParam(
              params.error as
                | string
                | ParsedQs
                | string[]
                | ParsedQs[]
                | undefined
            ),
          }),
          content: parseQueryParam(
            params.error_description as
              | string
              | ParsedQs
              | string[]
              | ParsedQs[]
              | undefined
          ),
        });
        break;
    }
  }, [params.error_description]);

  useTimeout(() => {
    props.onContinue();
  }, 3000);

  if (isLoading) {
    return <Loader overlay />;
  }

  if (params.error) {
    return (
      <AuthLayout>
        <AuthHeading title={t("Welcome")} />
        <SectionMessage
          icon="error"
          sectionTitle={customErrorMessage?.title}
          type="danger"
        >
          {customErrorMessage?.content}
        </SectionMessage>
        <PrimaryButton
          fullWidth
          onPress={async () => {
            await logout();
          }}
          size="large"
        >
          {t("Logout")}
        </PrimaryButton>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthHeading title={t("Authentication Successful")} />
      <Loader />
    </AuthLayout>
  );
}
